/**
 * Hooks personalizados para DataService en componentes React
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import dataService from '../services/dataService';

// Hook para inicializar el servicio de datos
export function useDataService() {
    const [status, setStatus] = useState({
        initialized: false,
        isOnline: true,
        loading: true
    });
    const [error, setError] = useState(null);
    const initializedRef = useRef(false);

    useEffect(() => {
        let mounted = true;
        
        const init = async () => {
            try {
                await dataService.init();
                if (mounted) {
                    setStatus(dataService.getStatus());
                    setError(null);
                }
            } catch (err) {
                if (mounted) {
                    setError(err.message);
                    setStatus(prev => ({ ...prev, initialized: true, loading: false }));
                }
            }
        };

        init();

        // Escuchar cambios de conectividad
        const handleOnline = () => {
            if (mounted) setStatus(dataService.getStatus());
        };
        const handleOffline = () => {
            if (mounted) setStatus(dataService.getStatus());
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            mounted = false;
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const refreshStatus = useCallback(() => {
        setStatus(dataService.getStatus());
    }, []);

    return { status, error, refreshStatus, dataService };
}

// Hook para áreas
export function useAreas() {
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [source, setSource] = useState('local');

    const loadAreas = useCallback(async () => {
        try {
            setLoading(true);
            const { areas: data, source: src } = await dataService.getAreas();
            setAreas(data);
            setSource(src);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAreas();
    }, [loadAreas]);

    return { areas, loading, error, source, refetch: loadAreas };
}

// Hook para temas por área
export function useTemasByArea(areaId) {
    const [temas, setTemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [source, setSource] = useState('local');

    const loadTemas = useCallback(async () => {
        if (!areaId) {
            setTemas([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const { temas: data, source: src } = await dataService.getTemasByArea(areaId);
            setTemas(data);
            setSource(src);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [areaId]);

    useEffect(() => {
        loadTemas();
    }, [loadTemas]);

    return { temas, loading, error, source, refetch: loadTemas };
}

// Hook para preguntas por tema
export function usePreguntasByTema(temaId, options = {}) {
    const { count, random = true } = options;
    const [preguntas, setPreguntas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [source, setSource] = useState('local');

    const loadPreguntas = useCallback(async () => {
        if (!temaId) {
            setPreguntas([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            let data, src;
            if (count && random) {
                data = await dataService.getRandomPreguntas(temaId, count);
                src = 'local'; // Las aleatorias vienen del cache local
            } else {
                const result = await dataService.getPreguntasByTema(temaId);
                data = result.preguntas;
                src = result.source;
            }
            setPreguntas(data);
            setSource(src);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [temaId, count, random]);

    useEffect(() => {
        loadPreguntas();
    }, [loadPreguntas]);

    return { preguntas, loading, error, source, refetch: loadPreguntas };
}

// Hook para estadísticas
export function useEstadisticas() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadStats = useCallback(async (type = 'general', areaId = null) => {
        try {
            setLoading(true);
            let data;
            if (type === 'general') {
                data = await dataService.getEstadisticasGenerales();
            } else if (type === 'area' && areaId) {
                data = await dataService.getEstadisticasArea(areaId);
            }
            setStats(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    return { stats, loading, error, refetch: loadStats };
}

// Hook para quizzes de usuario
export function useUserQuizzes(userId) {
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadQuizzes = useCallback(async () => {
        if (!userId) {
            setQuizzes([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await dataService.getUserQuizzes(userId);
            setQuizzes(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const saveQuiz = useCallback(async (quizData) => {
        if (!userId) throw new Error('Usuario no autenticado');
        const quiz = await dataService.saveUserQuiz(userId, quizData);
        setQuizzes(prev => [...prev, quiz]);
        return quiz;
    }, [userId]);

    const deleteQuiz = useCallback(async (quizId) => {
        await dataService.deleteUserQuiz(quizId);
        setQuizzes(prev => prev.filter(q => q.id !== quizId));
    }, []);

    useEffect(() => {
        loadQuizzes();
    }, [loadQuizzes]);

    return { quizzes, loading, error, saveQuiz, deleteQuiz, refetch: loadQuizzes };
}

// Hook para progreso de usuario
export function useUserProgress(userId, temaId) {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadProgress = useCallback(async () => {
        if (!userId || !temaId) {
            setProgress(null);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await dataService.getUserProgress(userId, temaId);
            setProgress(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId, temaId]);

    const saveProgress = useCallback(async (progressData) => {
        if (!userId || !temaId) throw new Error('Datos incompletos');
        const data = await dataService.saveUserProgress(userId, temaId, progressData);
        setProgress(data);
        return data;
    }, [userId, temaId]);

    useEffect(() => {
        loadProgress();
    }, [loadProgress]);

    return { progress, loading, error, saveProgress, refetch: loadProgress };
}

// Hook para resultados de quiz
export function useQuizResults(userId) {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadResults = useCallback(async () => {
        if (!userId) {
            setResults([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await dataService.getUnsyncedQuizResults(userId);
            setResults(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const saveResult = useCallback(async (resultData) => {
        if (!userId) throw new Error('Usuario no autenticado');
        const result = await dataService.saveQuizResult(userId, resultData);
        setResults(prev => [...prev, result]);
        return result;
    }, [userId]);

    const markSynced = useCallback(async (id) => {
        await dataService.markQuizResultSynced(id);
        setResults(prev => prev.map(r => r.id === id ? { ...r, synced: true } : r));
    }, []);

    useEffect(() => {
        loadResults();
    }, [loadResults]);

    return { results, loading, error, saveResult, markSynced, refetch: loadResults };
}

// Hook para sincronización manual
export function useSync() {
    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);
    const [error, setError] = useState(null);

    const forceSync = useCallback(async () => {
        setSyncing(true);
        setError(null);
        try {
            await dataService.forceSync();
            setLastSync(new Date().toISOString());
        } catch (err) {
            setError(err.message);
        } finally {
            setSyncing(false);
        }
    }, []);

    const syncPending = useCallback(async () => {
        setSyncing(true);
        setError(null);
        try {
            await dataService.syncPending();
        } catch (err) {
            setError(err.message);
        } finally {
            setSyncing(false);
        }
    }, []);

    return { syncing, lastSync, error, forceSync, syncPending };
}

export default {
    useDataService,
    useAreas,
    useTemasByArea,
    usePreguntasByTema,
    useEstadisticas,
    useUserQuizzes,
    useUserProgress,
    useQuizResults,
    useSync
};